import { useMemo } from 'react';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { useAlignedChildTable } from '@/components/tables/useAlignedChildTable';
import { formatNumber, pnlClass } from '@/utils/format';
import type { NetOpenPositionDto, NopLoginDetailDto, NopSectionDto } from '@/api/types/consolidatedExposure';

const money = (v: number | null | undefined, d = 2) => (v == null || Number.isNaN(v) ? '' : formatNumber(v, d));
const pnl = (v: number) => <span className={v < 0 ? 'text-danger' : 'text-success'}>{money(v, 2)}</span>;

// NOP positions table for a stress-test section (Clients/Firm/LP/R&T). Mirrors the
// legacy _ClientsNop column set; loginDetails is not a column but expands into a
// login-breakdown child row (first-column expand icon), like the live NOP table.
export function StressNopTable({ section, isCcy, excelName, card = true }: { section: NopSectionDto | null; isCcy: boolean; excelName: string; card?: boolean }) {
  const rows = useMemo(() => [...(section?.positions ?? [])].sort((a, b) => b.breachLevel - a.breachLevel), [section]);

  const columns: DataGridColumn<NetOpenPositionDto>[] = [
    { key: 'symbol', title: 'Instrument', className: 'text-start', value: (r) => r.symbol },
    isCcy
      ? { key: 'baseNotionalInCcy', title: 'Exposure (CCY)', className: 'text-end', value: (r) => r.baseNotionalInCcy ?? 0, cell: (r) => money(r.baseNotionalInCcy, 2) }
      : { key: 'netVolume', title: 'Net Volume', className: 'text-end', value: (r) => r.netVolume, cell: (r) => money(r.netVolume, 3) },
    { key: 'openPrice', title: 'Average Price', className: 'text-end', value: (r) => r.openPrice, cell: (r) => money(r.openPrice, r.digits || 5) },
    { key: 'floatingPnL', title: isCcy ? 'Stressed Floating PnL (CCY)' : 'Stressed Floating PnL', className: 'text-end', value: (r) => r.floatingPnL, cell: (r) => pnl(r.floatingPnL) },
    { key: 'originalFloatingPnL', title: isCcy ? 'Original Floating PnL (CCY)' : 'Original Floating PnL', className: 'text-end', value: (r) => r.originalFloatingPnL, cell: (r) => pnl(r.originalFloatingPnL) },
    { key: 'pnlDifference', title: 'PnL Difference', className: 'text-end', value: (r) => r.pnlDifference, cell: (r) => pnl(r.pnlDifference) },
    { key: 'bid', title: 'Last Price', className: 'text-end', value: (r) => r.bid, cell: (r) => money(r.bid, r.digits || 5) },
    { key: 'connector', title: 'Connector', className: 'text-start', value: (r) => r.connector },
    { key: 'concentration', title: 'Concentration', className: 'text-end', value: (r) => r.concentration, cell: (r) => (r.concentration == null ? '—' : `${money(r.concentration, 2)}%`) },
  ];

  // Leading empty cell aligns with the expand-control column.
  const totalsRow = section ? (
    <tr className="fw-semibold">
      <td />
      <td className="text-start">Total</td>
      <td className="text-end">{money(section.totalExposure, 2)}</td>
      <td className="text-end">-</td>
      <td className="text-end">{money(section.totalFloatingPnl, 2)}</td>
      <td className="text-end">{money(section.totalOriginalFloatingPnl, 2)}</td>
      <td className="text-end">{money(section.totalPnlDifference, 2)}</td>
      <td className="text-end">-</td>
      <td className="text-end">-</td>
      <td className="text-end">-</td>
    </tr>
  ) : null;

  return (
    <DataGrid
      columns={columns}
      data={rows}
      rowKey={(r) => `${r.symbol}-${r.connectorId}`}
      pageSize={5}
      card={card}
      includeExcel
      excelFileName={excelName}
      footerTop={totalsRow}
      rowClassName={(r) => (r.breachLevel > 0 ? 'threshold-breach' : '')}
      expandable={(r) => <LoginDetails details={r.loginDetails} isCcy={isCcy} digits={r.digits || 2} />}
      emptyMessage="No data."
    />
  );
}

function LoginDetails({ details, isCcy, digits }: { details: NopLoginDetailDto[]; isCcy: boolean; digits: number }) {
  const tableRef = useAlignedChildTable();
  if (!details?.length) return <em className="text-muted ms-2">No login details available</em>;
  // No title, no header — rows mirror the outer columns (legacy NopStressChildRows);
  // column widths are synced to the parent header by useAlignedChildTable.
  return (
    <div className="nop-detail-wrapper" style={{ maxHeight: 300, overflow: 'auto' }}>
      <table ref={tableRef} className="nop-inner-table table table-hover text-center my-1 mx-0">
        <tbody>
          {details.map((d, i) => (
            <tr key={`${d.platformId}:${d.login}:${i}`}>
              <td />
              <td className="text-start">{d.uniqueId || d.login}</td>
              <td className="font-monospace">{money(isCcy ? d.baseNotional ?? 0 : d.netVolume, isCcy ? 2 : digits)}</td>
              <td className="font-monospace">{money(d.averagePrice, digits)}</td>
              <td className="font-monospace"><span className={pnlClass(d.pnl)}>{money(d.pnl, 3)}</span></td>
              <td className="font-monospace"><span className={pnlClass(d.originalPnl)}>{money(d.originalPnl, 3)}</span></td>
              <td className="font-monospace"><span className={pnlClass(d.pnl - d.originalPnl)}>{money(d.pnl - d.originalPnl, 3)}</span></td>
              <td />
              <td>{d.platform || '—'}</td>
              <td className="font-monospace">{d.concentration != null ? `${money(d.concentration, 2)}%` : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
